import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";


import { useParams } from "react-router-dom";

import DietaryPreferenceFormModal from "./DietaryPreferenceFormModal";
import Button from "../../../../../components/ui/Button";
import Card from "../../../../../components/ui/Card";
import ConfirmDeleteModal from "../../../../../components/ui/ConfirmDeleteModal";
import { useTheme } from "../../../../../context/ThemeContext";
import { addNotification } from "../../../../../store/slices/uiSlice";
import { DietaryPreference } from "../../../../../types/dietary";
import { getDisplayDietaryName, getDisplayDietaryDescription } from "../../../../../utils/dietaryHelper";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import {
  selectDietaryPreferences,
  selectDietaryLoading
} from "../../../../../store/selectors";
import {
  fetchDietaryPreferences,
  createDietaryPreference as createDietaryPreferenceAction,
  updateDietaryPreference as updateDietaryPreferenceAction,
  deleteDietaryPreference as deleteDietaryPreferenceAction
} from "../../../../../store/slices/dietarySlice";



const DietaryPreferencesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const { branchId } = useParams<{ branchId: string }>();

  // Get data from Redux
  const preferences = useAppSelector(selectDietaryPreferences);
  const isLoading = useAppSelector(selectDietaryLoading);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPreference, setEditingPreference] = useState<DietaryPreference | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [preferenceToDelete, setPreferenceToDelete] = useState<DietaryPreference | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load dietary preferences on mount
  useEffect(() => {
    if (branchId) {
      dispatch(fetchDietaryPreferences(branchId));
    }
  }, [branchId, dispatch]);

  const handleAddPreference = () => {
    setEditingId(null);
    setEditingPreference(null);
    setFormModalOpen(true);
  };
  const handleEditPreference = (preference: DietaryPreference) => {
    setEditingId(preference.id);
    setEditingPreference(preference);
    setFormModalOpen(true);
  };
  const handleFormSubmit = async (data: Pick<DietaryPreference, 'name' | 'description' | 'ar'>) => {
    setIsSubmitting(true);
    try {
      if (!branchId) return;

      if (editingId) {
        // Update existing preference
        await dispatch(updateDietaryPreferenceAction({ id: editingId, data })).unwrap();

        dispatch(addNotification({
          type: 'success',
          title: t('dietaryPreferences.updateSuccess'),
          message: t('dietaryPreferences.updateSuccess'),
          duration: 3000
        }));
      } else {
        // Create new preference
        await dispatch(createDietaryPreferenceAction({ branchId, data })).unwrap();

        dispatch(addNotification({
          type: 'success',
          title: t('dietaryPreferences.addSuccess'),
          message: t('dietaryPreferences.addSuccess'),
          duration: 3000
        }));
      }

      // Redux already updated state - just close modal
      setFormModalOpen(false);
      setEditingPreference(null);
      setEditingId(null);
    } catch (error: any) {
      dispatch(addNotification({
        type: 'error',
        title: t('common.error') || 'Error',
        message: error.message || t('dietaryPreferences.addError'),
        duration: 3000
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (preference: DietaryPreference) => {
    setPreferenceToDelete(preference);
    setDeleteModalOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!preferenceToDelete) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteDietaryPreferenceAction(preferenceToDelete.id)).unwrap();

      // Redux already removed from state - just close modal
      setDeleteModalOpen(false);
      setPreferenceToDelete(null);

      dispatch(addNotification({
        type: 'success',
        title: t('dietaryPreferences.deleteSuccess') || 'Deleted',
        message: t('dietaryPreferences.deleteSuccess'),
        duration: 3000
      }));
    } catch (error: any) {
      dispatch(addNotification({
        type: 'error',
        title: t('common.error') || 'Error',
        message: error.message || t('dietaryPreferences.deleteError'),
        duration: 3000
      }));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={`px-4 sm:px-6 lg:px-8 py-8 ${theme === "dark" ? "text-gray-100" : "text-gray-900"
        }`}
    >
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t("dietaryPreferences.title")}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t("dietaryPreferences.subtitle")}
          </p>
        </div>

        {/* Add Button */}
        <div className="mb-6">
          <Button
            onClick={handleAddPreference}
            leftIcon={<FiPlus />}
            className="bg-primary hover:bg-primary/90"
          >
            {t("dietaryPreferences.addPreference")}
          </Button>
        </div>
      </div>

      {/* Content */}
      {
        isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : preferences.length === 0 ? (
          <Card className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {t("dietaryPreferences.noPreferences")}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {preferences.map((preference) => (
              <div
                key={preference.id}
                className="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 dark:border-gray-700"
              >
                {/* Gradient Accent Bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />
                
                {/* Card Content */}
                <div className="p-5 pt-6">
                  {/* Header with Actions */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                        {getDisplayDietaryName(preference, i18n.language)}
                      </h3>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => handleEditPreference(preference)}
                        className="p-2 rounded-lg text-gray-500 hover:text-primary hover:bg-[rgba(255,102,0,0.1)] dark:text-gray-400 dark:hover:text-primary dark:hover:bg-[rgba(255,102,0,0.2)] transition-colors cursor-pointer"
                        title={t("dietaryPreferences.edit")}
                      >
                        <FiEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(preference)}
                        className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors hover:cursor-pointer"
                        title={t("dietaryPreferences.delete")}
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 line-clamp-3 leading-relaxed">
                    {getDisplayDietaryDescription(preference, i18n.language) || (
                      <span className="italic text-gray-400 dark:text-gray-500">
                        {t("dietaryPreferences.noDescription")}
                      </span>
                    )}
                  </p>
                </div>
                
                {/* Bottom Decorative Element */}
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-primary/5 to-transparent rounded-tl-full pointer-events-none" />
              </div>
            ))}
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setPreferenceToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t("dietaryPreferences.deletePreference")}
        message={t("dietaryPreferences.deleteConfirmation")}
        isLoading={isDeleting}
      />

      {/* Add/Edit Form Modal */}
      <DietaryPreferenceFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingPreference(null);
          setEditingId(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={editingPreference ? {
          name: editingPreference.name,
          description: editingPreference.description || "",
          ar: editingPreference.ar || { name: "", description: "" }
        } : undefined}
        isLoading={isSubmitting}
      />
    </div >
  );
};

export default DietaryPreferencesPage;
